import { useState, useEffect } from "react";
import "./ExchangeForVariant.css";
import { ReturnStates } from "./ReturnStates";
import { ItemCard, Item, getDiscountedPrice, VariantCard } from "../shared/ProductVariantCards";
import { BsX } from 'react-icons/bs';
import NavButtons from "./NavButtons";
import React from "react";
import { ShopifyProduct, ShopifyVariant } from "./ProductResponse";
import ChipSelector from "./ChipSelector";
import InfoCard from "../shared/InfoCard";

interface ExchangeForVariantProps {
    selectedItemToReturn: Item;
    selectedVariant: ShopifyVariant | null;
    orderDiscount?: string;
    orderTotal?: number;
    setSelectedVariant: (variant: ShopifyVariant | null) => void;
    onStepChange: (step: ReturnStates) => void;
    handleCancel: (isBack: Boolean) => void;
    shopifyProducts: ShopifyProduct[];
    exchangeProduct: ShopifyProduct | null;
}

type VariantOptionMap = ShopifyVariant['optionMap'];

export enum ExchangeType {
    REFUND = 'refund',
    EXCHANGE = 'exchange',
}

const ExchangeForVariant = ({
    selectedItemToReturn,
    selectedVariant,
    orderDiscount,
    orderTotal,
    setSelectedVariant,
    onStepChange,
    handleCancel,
    shopifyProducts,
    exchangeProduct
}: ExchangeForVariantProps) => {

    const [selectedVariantOptionMap, setSelectedVariantOptionMap] = useState<VariantOptionMap>({});
    const [currentProduct, setCurrentProduct] = useState<ShopifyProduct | null>(null);
    const [selectionError, setSelectionError] = useState(false);

    // Load variant options
    useEffect(() => {
        const foundProduct = shopifyProducts.find((p) => p.id === selectedItemToReturn.product_id) || null;

        const currentProduct = exchangeProduct ?? foundProduct;
        setCurrentProduct(currentProduct);

        // Select variant by default if only one exists
        if (currentProduct?.variants?.length === 1) {
            const variant = currentProduct?.variants?.[0];
            if (variant) {
                // Fallback to product image if variant image is missing
                if (!variant.image_id || variant.image_id.trim() === '') {
                    variant.image_id = currentProduct.image?.src ?? '';
                }

                setSelectedVariantOptionMap(variant.optionMap);
                setSelectedVariant(variant);
            }
        }
    }, [selectedItemToReturn, shopifyProducts, exchangeProduct]);

    useEffect(() => {
        if (!currentProduct) return;

        const match = currentProduct.variants.find((variant) => {
            const keys = Object.keys(variant.optionMap);
            return keys.every((key) => variant.optionMap[key] === selectedVariantOptionMap[key]);
        });

        setSelectedVariant(match ?? null);
    }, [selectedVariantOptionMap, currentProduct, setSelectedVariant]);

    const updateSelectedVariant = (optionTitle: string, optionValue: string | null) => {
        setSelectedVariantOptionMap((prev) => ({
            ...prev,
            [optionTitle]: optionValue,
        }));
        setSelectionError(false);
    };

    const CloseButton = (
        <div className="close-button sticky-top pt-2" onClick={() => handleCancel(false)}>
            <BsX size={40} />
        </div>
    );

    return (
        <div className="card">
            {CloseButton}
            <div className="product-info-container d-flex-column flex-sm-row justify-content-center align-items-start gap-5">

                <div className="d-flex-column align-items-center align-self-center" style={{ flex: 1 }}>
                        <VariantCard
                            variant={selectedVariant}
                            cardStyle={false}
                            shopifyProduct={currentProduct as ShopifyProduct}
                            selected={true}
                        />
                </div>

                <div className="responsive-divider" />

                {/* Variant Options */}
                <div style={{ flex: 2, whiteSpace: "nowrap" }}>
                    {currentProduct && (
                        <>
                            <h1 id='scroll-target' className="variant-option-title">Select an option</h1>
                            {currentProduct.variantTitleToOptions &&
                                Object.entries(currentProduct.variantTitleToOptions).map(([title, options]) => {
                                    const otherSelections = { ...selectedVariantOptionMap };
                                    delete otherSelections[title];

                                    const validOptions = new Set<string>();

                                    currentProduct.variants.forEach((variant) => {
                                        const match = Object.entries(otherSelections).every(
                                            ([otherTitle, val]) => variant.optionMap[otherTitle] === val
                                        );
                                        if (match) {
                                            const value = variant.optionMap[title];
                                            if (value != null) {
                                                validOptions.add(value);
                                            }
                                        }
                                    });

                                    const disabledOptions = new Set(options.filter((opt) => !validOptions.has(opt)));

                                    return (
                                        <div key={title} className="variant-option">
                                            <h4 className="option-title">{title}</h4>
                                            <ChipSelector
                                                options={options}
                                                defaultSelection={selectedVariantOptionMap?.[title] ?? null}
                                                onSelect={(option) => updateSelectedVariant(title, option)}
                                                disabledOptions={disabledOptions}
                                            />
                                        </div>
                                    );
                                })
                            }
                        </>
                    )}
                </div>
            </div>

            {/* Footer Buttons & Price */}
            <div className="d-flex-column align-items-center sticky-bottom mt-5 pt-0" style={{ background: 'white' }}>
                <div className="scroll-fade d-flex align-items-end">

                    {selectionError && <div className="no-selection-error mb-2">Please select an option to proceed.</div>}
                </div>
                <div className="d-flex justify-content-end align-items-end w-100">
                    <NavButtons
                        style={{ background: 'white' }}
                        nextButtonText="Save exchange selection"
                        handleBack={() => {
                            setSelectedVariant(null);
                            handleCancel(true);
                        }}
                        handleNext={() => onStepChange(ReturnStates.RETURN_CONFIRMATION)}
                        disabled={!selectedVariant}
                        onDisabledClick={() => setSelectionError(true)}
                    />
                </div>
            </div>

        </div>
    );
};

export default ExchangeForVariant;
